/**
 * Localized text for API errors. The server sends a stable `code` (+ `details` / field `params`) and a
 * safe `message`. See docs/i18n.md#errors.
 *
 * - English (the source language): the server's specific message is shown as-is.
 * - Other languages: the translation for the code, interpolated with details/params.
 * - No translation for the code: the server's message; with no message, a generic localized text.
 */
import { DEFAULT_LOCALE, currentLocale, i18next } from '@/shared/i18n';
import { formatMoney, isDecimalString } from '@/shared/lib/money';

import { isApiError, type FieldError } from './errors';

const isSource = () => (currentLocale().split('-')[0] ?? DEFAULT_LOCALE) === DEFAULT_LOCALE;

/** Money params arrive as decimal strings ("15000.00") and are shown formatted ("15,000.00"). */
const MONEY_PARAMS = new Set(['minimum', 'maximum', 'amount', 'limit']);

function scalarValues(values: Record<string, unknown> | undefined): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(values ?? {})) {
    if (typeof v === 'string' && MONEY_PARAMS.has(k) && isDecimalString(v)) out[k] = formatMoney(v);
    else if (typeof v === 'string' || typeof v === 'number') out[k] = v;
  }
  return out;
}

/** Message for any thrown error, for an alert or form summary. */
export function apiErrorMessage(error: unknown): string {
  if (!isApiError(error)) return i18next.t('errors.unknown', { ns: 'common' });
  if (error.isNetwork) return i18next.t('errors.network', { ns: 'common' });
  const key = `errors.server.${error.code}`;
  const translated = i18next.exists(key, { ns: 'common' });
  if (error.message && (isSource() || !translated)) return error.message;
  if (translated) return i18next.t(key, { ns: 'common', ...scalarValues(error.details) });
  return i18next.t('errors.unknown', { ns: 'common' });
}

/** Message for one field error. Codes that need values (range, min, max…) fall back to "invalid" without params. */
export function fieldErrorMessage(fe: FieldError): string {
  const params = scalarValues(fe.params);
  const key = `validation.server.${fe.code.toLowerCase()}`;
  const withParams = `${key}_params`;
  if (fe.message && isSource()) return fe.message;
  if (Object.keys(params).length && i18next.exists(withParams, { ns: 'common' })) return i18next.t(withParams, { ns: 'common', ...params });
  if (i18next.exists(key, { ns: 'common' })) return i18next.t(key, { ns: 'common', ...params });
  return fe.message || i18next.t('validation.server.invalid', { ns: 'common' });
}
